import React from "react";
import { PageLayout, Clause, Callout, Bullets } from "@/components/PageLayout";

export default function TermsOfServiceEn() {
  return (
    <PageLayout
      testId="terms-of-service-page"
      eyebrow="Legal"
      title="Terms of Service"
      updated="1 June 2026"
      intro="These terms govern your use of Seleksa. They set out how the subscription works, how payments are treated, and — importantly for a platform that sits between a studio and its clients — exactly where our responsibility ends and the studio's begins."
    >
      <Clause n={1} title="Who these terms bind">
        <p>
          Seleksa is software provided to photography studios and freelance photographers ("the Studio"). By creating an
          account, subscribing, or using any part of the service, the Studio accepts these terms. Where a Studio invites
          its own customers ("the Client") into a gallery, those Clients use the service under the Studio's engagement,
          not under a separate contract with Seleksa.
        </p>
        <p>
          The person accepting these terms confirms they are authorised to bind the Studio, and that they are of legal
          age to enter a contract in Indonesia.
        </p>
      </Clause>

      <Clause n={2} title="The subscription">
        <p>
          Seleksa is sold as a recurring software subscription. Each plan grants access to the platform for a fixed
          period — typically monthly or annual — beginning when payment is confirmed.
        </p>
        <Bullets items={[
          "New studios may be offered a free trial. Trials are limited in duration and in the number of active galleries, and are provided for evaluation only.",
          "When a subscription period ends without renewal, the account is locked: existing galleries remain viewable, but creating or modifying galleries requires an active plan.",
          "Subscriptions do not renew automatically by silent card charge. We notify you before expiry and you choose to renew.",
          "Plan prices are quoted in Indonesian Rupiah (IDR). We may change pricing for future periods, but never retroactively for a period you have already paid for.",
        ]} />
        <p>
          Promotional and affiliate codes reduce the price of the period they are applied to. They cannot be applied
          retroactively, combined unless expressly stated, or exchanged for cash.
        </p>
      </Clause>

      <Clause n={3} title="Payments and finality">
        <p>
          Subscription payments are processed by Midtrans for Indonesian studios, or by PayPal for studios paying from
          outside Indonesia. Midtrans methods include QRIS, virtual account, e-wallet and
          bank transfer; PayPal payments are charged in US dollars at the fixed USD price shown for each plan. Seleksa
          never receives or stores your card or banking credentials, and neither provider is authorised to charge you
          again automatically — each period is a separate payment you choose to make.
        </p>
        <Callout tone="red">
          All payments are final. Once a subscription payment is confirmed by Midtrans or PayPal, it is non-refundable and
          non-reversible, and the corresponding subscription period is considered delivered in full.
        </Callout>
        <p>
          This reflects the nature of the product: access is granted immediately and in full on payment, and the
          software cannot be returned. We therefore ask you to use the free trial to satisfy yourself that Seleksa fits
          your workflow before you pay.
        </p>
        <p>
          We will, however, act in good faith where we are at fault. If a payment is charged twice for the same period,
          or a confirmed payment fails to activate your subscription, contact us and we will correct it. Chargeback
          attempts made without first contacting us may result in immediate suspension of the account.
        </p>
      </Clause>

      <Clause n={4} title="The Studio's relationship with its own Clients">
        <p>
          This is the most important clause in this document. Seleksa provides the tool through which a Studio collects
          selections; it is not a party to the commercial relationship between the Studio and its Client.
        </p>
        <Callout tone="red">
          The Studio is solely and exclusively responsible for resolving all disputes with its own Clients, and for
          fulfilling every photography, editing, printing, framing and delivery obligation it has promised them.
          Seleksa carries no liability whatsoever for these matters.
        </Callout>
        <p>Without limiting the above, the Studio alone is responsible for:</p>
        <Bullets items={[
          "The quality, colour accuracy, cropping, physical dimensions and durability of any printed or framed product delivered to a Client.",
          "Production timelines, courier arrangements, delivery failures, loss or damage in transit, and any reprinting required as a result.",
          "Collecting money owed by a Client, including any charge for extra edits or extra prints that Seleksa has calculated. Seleksa does not process Client payments and never holds Client money.",
          "The accuracy of the prices, quotas, package inclusions and print sizes it configures. Figures Seleksa displays are arithmetic performed on the Studio's own numbers.",
          "Every complaint, refund request, cancellation, or claim brought by a Client, regardless of whether the Client encountered the issue inside a Seleksa gallery.",
          "Having the right to display each photograph it uploads or references, and honouring any model release, licence or consent that applies.",
        ]} />
        <p>
          The Studio agrees to indemnify and hold Seleksa harmless against any claim, demand, loss or legal cost arising
          from a dispute with its Clients, from the content of its galleries, or from its failure to deliver what it has
          promised.
        </p>
      </Clause>

      <Clause n={5} title="The Auto-Sorter scripts — run at your own risk">
        <p>
          Seleksa can generate automation scripts (<code>.bat</code> for Windows, <code>.sh</code> for macOS and Linux)
          that reorganise a Studio's photograph files into folders matching a Client's selection. These scripts execute
          on the Studio's own hardware, under the Studio's own user account, with whatever permissions that account
          holds.
        </p>
        <Callout tone="red">
          The Studio assumes full and sole responsibility for executing these scripts. They operate directly on your
          file system and can move, copy or overwrite files. Seleksa accepts no liability for data loss, file
          corruption, misplaced photographs, or any other damage arising from running them.
        </Callout>
        <p>Before running any generated script, the Studio should:</p>
        <Bullets items={[
          "Maintain a complete, verified backup of the shoot — ideally a copy on separate physical media.",
          "Open and read the script. It is plain text and its actions are legible; never run a file you have not inspected.",
          "Confirm the target folder paths are correct for your machine before execution.",
          "Test on a small sample folder the first time you use it on a new computer or folder structure.",
        ]} />
        <p>
          Seleksa cannot detect the state of your local disks, whether your paths are correct, whether you hold a valid
          backup, or whether another program is using the same files. These conditions are outside our control and are
          therefore your responsibility.
        </p>
      </Clause>

      <Clause n={6} title="Acceptable use">
        <p>The Studio agrees not to:</p>
        <Bullets items={[
          "Upload or reference unlawful content, or content it lacks the rights to display.",
          "Resell, sublicense, white-label or otherwise pass off the platform as its own software, except where a plan expressly allows custom branding.",
          "Attempt to access another studio's data, probe or bypass access controls, or interfere with the platform's operation.",
          "Circumvent trial limits by creating multiple accounts, or share one subscription across genuinely separate businesses.",
          "Upload malware, or use the service to distribute anything harmful to recipients.",
        ]} />
        <p>
          We may suspend or terminate an account that breaches this clause. Where the breach is serious — unlawful
          content, or an attack on the platform or its other users — we may do so immediately and without refund.
        </p>
      </Clause>

      <Clause n={7} title="Availability">
        <p>
          We work to keep Seleksa available and fast, but we do not guarantee uninterrupted service. Maintenance,
          third-party outages — Google Drive, Midtrans, PayPal, email delivery, hosting — and events outside our control can
          all interrupt access. Seleksa is provided on an "as is" and "as available" basis, without warranty of
          uninterrupted or error-free operation.
        </p>
        <p>
          Because a Studio's original files remain in the Studio's own storage, a Seleksa outage does not put your
          photographs at risk. We nevertheless recommend not scheduling a Client deadline so tightly that a few hours
          of downtime would breach it.
        </p>
      </Clause>

      <Clause n={8} title="Limitation of liability">
        <p>
          To the fullest extent permitted by Indonesian law, Seleksa's total aggregate liability to a Studio for any
          claim arising out of or relating to the service is limited to the subscription fees that Studio actually paid
          in the three months immediately preceding the event giving rise to the claim.
        </p>
        <p>
          Seleksa is not liable for indirect, incidental, special or consequential loss, including lost profit, lost
          business, lost bookings, reputational harm, or the cost of reshooting a session — whether or not such loss
          was foreseeable.
        </p>
        <p>
          Nothing in these terms excludes liability that cannot lawfully be excluded, including liability for fraud or
          for death or personal injury caused by negligence.
        </p>
      </Clause>

      <Clause n={9} title="Intellectual property">
        <p>
          The Studio retains all rights in its photographs. Nothing here transfers ownership of your work to us, and we
          do not license, sell, publish or use your galleries for our own marketing or for training machine-learning
          models. We hold a narrow, purely technical permission to store and display your images for the sole purpose
          of operating the service for you.
        </p>
        <p>
          Seleksa retains all rights in the platform itself: its software, interface, branding, generated scripts and
          documentation.
        </p>
      </Clause>

      <Clause n={10} title="Termination">
        <p>
          A Studio may stop using Seleksa at any time; the subscription then simply lapses at the end of the paid
          period. We may terminate an account for material breach of these terms, or for non-payment.
        </p>
        <p>
          Before terminating, export what you need: summaries, invoices and sorter scripts. After termination, data is
          removed in line with the retention periods in our Privacy Policy.
        </p>
      </Clause>

      <Clause n={11} title="Governing law and changes">
        <p>
          These terms are governed by the laws of the Republic of Indonesia, and any dispute is subject to the
          jurisdiction of the courts of Medan, North Sumatra.
        </p>
        <p>
          We may amend these terms as the service develops, revising the date at the top. Material changes will be
          notified to subscribing studios by email; continuing to use Seleksa after a change takes effect constitutes
          acceptance.
        </p>
        <p>
          Questions can be sent to{" "}
          <a href="mailto:seleksa.com@gmail.com" className="text-[#D4AF37] hover:underline">seleksa.com@gmail.com</a>.
        </p>
      </Clause>
    </PageLayout>
  );
}
